import React from "react";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatAmount, formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ProductListItem from "../../components/ProductListItem";
import CloseIcon from "../../icons/x.svg";
import EyeIcon from "../../icons/eyeB.svg";

const STATUS_COLORS = {
  CREATED: "#0283F4",
  UNDER_APPROVAL: "#A8ADC3",
  VALIDATED: "#97DFFC",
  DELIVERED: "#06D6A0",
  DECLINED: "#EF476F",
  CANCELLED: "#38485C",
  EN_ATTENTE: "#FEA26F",
};

const InfoCard = ({ label, value }) => (
  <div className="flex flex-col">
    <p className="text-[#707070] text-[14px] font-semibold">{label}</p>
    <p className="text-[#38485C] text-[20px] font-semibold">{value}</p>
  </div>
);

const OrderDetailsPopup = ({ order, onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (!order) return null;

  const getStatusLabel = (status) => t(`order_status.${status}`) || status;

  const handleConsultOrder = (order) => {
    history.push({
      pathname: `/orders/details/${order.id}`,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
      <div className="relative bg-[#F9FAFC] h-[750px] rounded-lg shadow-lg flex flex-col max-w-[1200px] p-8 ">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-full"
          onClick={onClose}
        >
          <img src={CloseIcon} alt="Close" />
          </button>

        {/* Title and Status Section */}
        <div className="mb-4">
          <h1 className="text-[24px] font-semibold text-black">{t(`titles.order_details`)}</h1>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <p className="text-primary text-[18px] font-semibold">
                {order.client?.name || ""} {order.client?.code || ""}
              </p>
              <span
                className="text-white text-[12px] font-semibold px-2 py-1 rounded-full"
                style={{
                  backgroundColor: STATUS_COLORS[order.status] || "#A8ADC3",
                }}
              >
                {getStatusLabel(order.status)}
              </span>
            </div>
            <PrimaryButton
              label={t(`primaryButtons.consult_order`)}
              icon={EyeIcon}
              onClick={() => handleConsultOrder(order)}
            />
          </div>
        </div>

        {/* Order Information Section */}
        <div className="grid grid-cols-5 gap-4 mb-4 py-4 pl-[32px] bg-white rounded-lg  shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
          <InfoCard label={t(`rows.reference`).toUpperCase()} value={order.cartNumber} />
          <InfoCard label={t(`rows.creationDate`)} value={formatDateTime(order.createdAt)} />
          <InfoCard label={t(`rows.commercial`)} value={`${order.user.firstName} ${order.user.lastName}`} />
          <InfoCard label="STOCK" value={order.repo?.name} />
          <InfoCard label={t(`rows.paymentMode`)} value={order.paymentMode} />
        </div>

        {/* Comment Section */}
        <div className="py-4 pl-[32px] bg-white rounded-lg  shadow-[0px_6px_26px_rgba(98,107,125,0.07)] mb-4">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t(`rows.comment`)}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {order.comment || t('placeholder.no_comment')}
          </p>
        </div>

        {/* Products List Section */}
        <div className="flex-1 overflow-y-auto mb-4">
          <p className="text-[#ACAFC6] text-[16px] font-medium mb-4">{t(`fields_types.list`)}</p>
          {order.details.map((productDetail, index) => (
            <ProductListItem
              key={index}
              product={{
                reference: productDetail.product.reference,
                name: productDetail.product.name,
                quantity: productDetail.quantity,
                discount: productDetail.discount,
                unitPriceHT: productDetail?.product.priceHT,
                unitPriceTTC: productDetail?.product.priceTTC,
                priceHT: productDetail.priceHT,
              }}
            />
          ))}
        </div>

        {/* Totals Section */}
        <div className="flex items-center">
          <p className="text-[#231E29] text-[16px] font-medium">TOTAL HT:</p>
          <p className="text-[#043F88] text-[16px] font-bold p-1">{formatAmount(order.priceHT)}</p>
          <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">TOTAL TTC:</p>
          <p className="text-[#043F88] text-[16px] font-bold p-1">{formatAmount(order.priceTTC)}</p>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPopup;
